import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../components/motion";
import { Seo } from "../components/Seo";
import { EXPERIENCE, EDUCATION } from "../data/content";

const Experience = () => {
  const [active, setActive] = useState(0);

  const allStacks = useMemo(() => {
    const set = new Set();
    EXPERIENCE.forEach((e) => e.stack.forEach((s) => set.add(s)));
    return Array.from(set);
  }, []);

  const activeStack = EXPERIENCE[active].stack;

  return (
    <div data-testid="experience-page">
      <Seo path="/experience" description="20+ years of UX leadership — Yuva Kumar's career from UX Lead to Associate UX Director (APAC) at BORN/Tech Mahindra, scaling a design practice from 6 to 60+ across telecom, CPG, banking and enterprise." keywords="Yuva Kumar experience, UX Director career, design leadership timeline, Tech Mahindra UX" />
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal><p className="eyebrow mb-6">Experience</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="display text-5xl sm:text-8xl max-w-[18ch]" style={{ color: "var(--text)" }}>Two decades of leadership.</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg" style={{ color: "var(--muted-t)" }}>
              A chronological record of the teams built, practices scaled, and paradigms mastered. Select a chapter to explore.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16">
          {/* Timeline */}
          <div data-testid="timeline">
            {EXPERIENCE.map((exp, i) => {
              const isActive = i === active;
              return (
                <div key={i} className="relative pl-8 sm:pl-12 pb-2">
                  {/* rail */}
                  <span className="absolute left-0 top-2 bottom-0 w-px" style={{ background: "var(--line)" }} />
                  <button
                    onClick={() => setActive(i)}
                    data-testid={`timeline-item-${i}`}
                    className="text-left w-full py-7 border-t hairline transition-colors duration-300"
                  >
                    <span
                      className="absolute left-[-5px] top-9 h-[11px] w-[11px] rounded-full border-2 transition-colors duration-300"
                      style={{ background: isActive ? "var(--accent)" : "var(--bg)", borderColor: "var(--accent)" }}
                    />
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h2 className="display text-3xl sm:text-5xl transition-opacity" style={{ color: isActive ? "var(--accent)" : "var(--text)", opacity: isActive ? 1 : 0.75 }}>
                        {exp.company}
                      </h2>
                      <span className="text-sm" style={{ color: "var(--muted-t)" }}>{exp.period}</span>
                    </div>
                    <p className="mt-2 text-sm uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>{exp.role}</p>

                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <ul className="mt-6 space-y-3 max-w-2xl">
                            {exp.achievements.map((a, j) => (
                              <li key={j} className="flex gap-3 text-base leading-relaxed" style={{ color: "var(--muted-t)" }}>
                                <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--accent)" }} />
                                {a}
                              </li>
                            ))}
                          </ul>
                          <div className="mt-5 flex flex-wrap gap-2 lg:hidden">
                            {exp.stack.map((s) => (
                              <span key={s} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>{s}</span>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Sticky expanding sidebar */}
          <aside className="hidden lg:block" data-testid="stack-sidebar">
            <div className="sticky top-28 surface-card p-8">
              <p className="eyebrow mb-6">Now Mastering</p>
              <p className="text-xs mb-4" style={{ color: "var(--muted-t)" }}>{EXPERIENCE[active].company}</p>
              <div className="flex flex-wrap gap-2 mb-10">
                {allStacks.map((s) => {
                  const on = activeStack.includes(s);
                  return (
                    <motion.span
                      key={s}
                      animate={{ opacity: on ? 1 : 0.28, scale: on ? 1 : 0.96 }}
                      transition={{ duration: 0.4 }}
                      className="text-xs px-3 py-1.5 rounded-full border"
                      style={{
                        borderColor: on ? "var(--accent)" : "var(--line)",
                        color: on ? "var(--accent)" : "var(--muted-t)",
                        background: on ? "var(--accent-soft)" : "transparent",
                      }}
                    >
                      {s}
                    </motion.span>
                  );
                })}
              </div>

              <p className="eyebrow mb-4">Education</p>
              <ul className="space-y-4">
                {EDUCATION.map((e, i) => (
                  <li key={i}>
                    <p className="text-sm" style={{ color: "var(--text)" }}>{e.degree}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--muted-t)" }}>{e.school} · {e.period}</p>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Experience;
